/*
  component: layout-split-editor
  title: Layout Split Editor
  props: inline
  methods: setLayout, getLayout, setComponentCatalog, setSectionContent
  events: layout-change, layout-widget-change, layout-section-added
  deps: layout-split utils, layout-component-picker-modal
*/
import {
  migrateToV2,
  cloneLayout,
  collectSections,
  splitNode,
  assignSection,
  clearSection,
  mergeSplit,
  getNodeAt,
  validateLayoutClient
} from '../utils/layout-split.js';
import {
  catalogEntryToSection,
  sectionToCatalogKey,
  canAddCatalogEntry
} from '../utils/layout-component-catalog.js';
import { appendReturnTo } from '../utils/return-to-url.js';
import './layout-component-picker-modal.js';

class LayoutSplitEditor extends HTMLElement {
  constructor() {
    super();
    this._layout = migrateToV2(null);
    this._componentCatalog = [];
    this._content = new Map();
    this._instanceConfigValues = new Map();
    this._oauthStatus = new Map();
    this._inline = false;
    this._openMenuPath = null;
    this._pickerPath = null;
    this._pickerMode = null;
    this._mountListenersBound = false;
    this._suppressOutsideClick = false;
    this._outsideClickBound = null;
  }

  connectedCallback() {
    this._inline = this.hasAttribute('inline');
    this.render();
    this._bindMountListeners();
    this._stripOAuthQueryParams();
  }

  _stripOAuthQueryParams() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('widget_oauth') && !params.has('widget_oauth_error')) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('widget_oauth');
    url.searchParams.delete('widget_oauth_error');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }

  setLayout(doc) {
    this._layout = migrateToV2(doc);
    this.render();
    this._emitChange();
  }

  getLayout() {
    return cloneLayout(this._layout);
  }

  setComponentCatalog(entries) {
    this._componentCatalog = Array.isArray(entries) ? entries : [];
    this.render();
    this.refreshOAuthStatuses();
  }

  async refreshOAuthStatuses() {
    const userId = document.querySelector('user-profile')?.profileData?.user?.id;
    if (!userId) return;

    const sections = collectSections(this._layout.root).filter((s) => s.type === 'widget' && s.widgetId);
    await Promise.all(sections.map(async (section) => {
      const widget = this._getWidgetMeta(section.widgetId);
      if (!widget?.requiresOAuth && !widget?.oauthConfig) return;
      try {
        const res = await fetch(
          `/api/widget-instances/${encodeURIComponent(userId)}/${encodeURIComponent(section.id)}/oauth/status`,
          { credentials: 'include' }
        );
        if (res.ok) {
          this._oauthStatus.set(section.id, await res.json());
        }
      } catch {
        this._oauthStatus.set(section.id, { connected: false });
      }
    }));

    this.querySelectorAll('[data-oauth-section]').forEach((el) => {
      const sectionId = el.dataset.oauthSection;
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;
      const widget = this._getWidgetMeta(section.widgetId);
      el.outerHTML = this._renderWidgetOAuthPanel(section, widget);
    });
    this._stripOAuthQueryParams();
  }

  setSectionContent(sectionId, html) {
    this._content.set(sectionId, html);
    const slot = this.querySelector(`[data-section-id="${sectionId}"] .layout-split-editor-preview`);
    if (slot) {
      slot.innerHTML = html;
    }
  }

  clearSectionContent() {
    this._content.clear();
  }

  _emitChange() {
    const validation = validateLayoutClient(this._layout);
    this.dispatchEvent(new CustomEvent('layout-change', {
      bubbles: true,
      detail: {
        layout: this.getLayout(),
        valid: validation.valid,
        error: validation.error || null
      }
    }));
  }

  _parsePath(raw) {
    if (!raw) return [];
    return raw.split(',').map((n) => parseInt(n, 10)).filter((n) => !Number.isNaN(n));
  }

  getInstanceConfigs() {
    this._syncInstanceConfigFromDom();
    const layout = this.getLayout();
    const configs = [];
    for (const section of collectSections(layout.root)) {
      if (section.type !== 'widget' || !section.widgetId) continue;
      const values = this._instanceConfigValues.get(section.id);
      if (!values) continue;
      const config = Object.fromEntries(
        Object.entries(values).filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
      );
      if (Object.keys(config).length === 0) continue;
      configs.push({
        sectionId: section.id,
        widgetId: section.widgetId,
        config
      });
    }
    return configs;
  }

  _syncInstanceConfigFromDom() {
    this.querySelectorAll('[data-widget-config-field]').forEach((input) => {
      const sectionId = input.dataset.sectionId;
      const field = input.dataset.widgetConfigField;
      if (!sectionId || !field) return;
      if (!this._instanceConfigValues.has(sectionId)) {
        this._instanceConfigValues.set(sectionId, {});
      }
      const bag = this._instanceConfigValues.get(sectionId);
      bag[field] = input.value;
    });
  }

  _getWidgetMeta(widgetId) {
    const wid = parseInt(widgetId, 10);
    const entry = this._componentCatalog.find(
      (e) => e.sectionType === 'widget' && parseInt(e.widgetId, 10) === wid
    );
    if (!entry) return null;
    return {
      id: entry.widgetId,
      name: entry.name,
      status: entry.status,
      configSchema: entry.configSchema,
      requiresOAuth: entry.requiresOAuth,
      oauthConfig: entry.oauthConfig,
      oauthClientId: entry.oauthClientId || null,
      hasOAuthClientSecret: entry.hasOAuthClientSecret === true
    };
  }

  /**
   * Prefill widget instance config before render (e.g. when entering profile edit mode).
   * @param {Map<string, object>} configMap sectionId → public config fields
   */
  setInstanceConfigValues(configMap) {
    if (!(configMap instanceof Map)) return;
    for (const [sectionId, config] of configMap.entries()) {
      if (config && typeof config === 'object') {
        this._instanceConfigValues.set(sectionId, { ...config });
      }
    }
  }

  _renderWidgetConfigForm(section) {
    const widget = this._getWidgetMeta(section.widgetId);
    const schema = widget?.configSchema;
    const props = schema?.properties;
    if (!props || typeof props !== 'object') {
      return '';
    }

    const values = this._instanceConfigValues.get(section.id) || {};
    const fields = Object.entries(props).map(([key, def]) => {
      const title = def.title || key;
      const isSecret = def.secret === true;
      const isMarkdown = def.format === 'markdown';
      const val = values[key] !== null && values[key] !== undefined ? values[key] : '';
      const autocomplete = isSecret ? 'new-password' : 'off';

      if (isMarkdown && !isSecret) {
        return `
        <div class="form-group layout-widget-config-field">
          <label class="form-label" for="widget-cfg-${section.id}-${key}">${this._escapeHtml(title)}</label>
          <markdown-preview>
            <textarea
              id="widget-cfg-${section.id}-${key}"
              class="form-control"
              rows="6"
              maxlength="5000"
              placeholder="Write markdown…"
              autocomplete="${autocomplete}"
              data-section-id="${this._escapeHtml(section.id)}"
              data-widget-config-field="${this._escapeHtml(key)}"
            >${this._escapeHtml(val)}</textarea>
          </markdown-preview>
          ${def.description ? `<p class="form-text">${this._escapeHtml(def.description)}</p>` : ''}
        </div>
      `;
      }

      const inputType = isSecret ? 'password' : 'text';
      return `
        <div class="form-group layout-widget-config-field">
          <label class="form-label" for="widget-cfg-${section.id}-${key}">${this._escapeHtml(title)}</label>
          <input
            id="widget-cfg-${section.id}-${key}"
            class="form-control"
            type="${inputType}"
            autocomplete="${autocomplete}"
            data-section-id="${this._escapeHtml(section.id)}"
            data-widget-config-field="${this._escapeHtml(key)}"
            value="${this._escapeHtml(val)}"
          />
          ${def.description ? `<p class="form-text">${this._escapeHtml(def.description)}</p>` : ''}
        </div>
      `;
    }).join('');

    return `
      <div class="layout-widget-config card-body" data-widget-config-section="${this._escapeHtml(section.id)}">
        <p class="form-text">Configure this widget for your profile:</p>
        ${fields}
      </div>
    `;
  }

  _renderWidgetOAuthPanel(section, widget) {
    if (!widget?.requiresOAuth && !widget?.oauthConfig) return '';
    const status = this._oauthStatus.get(section.id) || { connected: false };
    const label = widget.oauthConfig?.serviceLabel || widget.name || 'service';
    if (status.connected) {
      const who = status.accountIdentifier
        ? ` as <strong>${this._escapeHtml(status.accountIdentifier)}</strong>`
        : '';
      return `
        <div class="layout-widget-oauth card-body" data-oauth-section="${this._escapeHtml(section.id)}">
          <p class="form-text">Connected${who}.</p>
          <button type="button" class="btn btn-secondary" data-action="oauth-disconnect" data-section-id="${this._escapeHtml(section.id)}">Disconnect</button>
        </div>
      `;
    }
    if (!widget.oauthClientId) {
      return `
        <div class="layout-widget-oauth card-body" data-oauth-section="${this._escapeHtml(section.id)}">
          <p class="form-text layout-widget-oauth-warn" role="alert">OAuth credentials are not configured for this widget yet. A site admin can add the client ID and secret in My Widgets or Admin → Profile Widgets.</p>
        </div>
      `;
    }
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    const connectUrl = `/api/widgets/oauth/authorize?widgetId=${section.widgetId}&sectionId=${encodeURIComponent(section.id)}&returnTo=${returnTo}`;
    return `
      <div class="layout-widget-oauth card-body" data-oauth-section="${this._escapeHtml(section.id)}">
        <p class="form-text">Connect your ${this._escapeHtml(label)} account to use this widget.</p>
        <p class="form-text layout-widget-oauth-save-hint">We'll save your layout first, then connect.</p>
        <button
          type="button"
          class="btn btn-primary"
          data-action="oauth-connect"
          data-widget-id="${section.widgetId}"
          data-section-id="${this._escapeHtml(section.id)}"
          data-connect-url="${connectUrl}"
        >Connect with ${this._escapeHtml(label)}</button>
      </div>
    `;
  }

  _applyLayout(nextRoot) {
    if (!nextRoot) return;
    this._layout = { version: 2, root: nextRoot };
    this._openMenuPath = null;
    this.render();
    this._emitChange();
  }

  _handleSplit(path, direction) {
    this._applyLayout(splitNode(this._layout.root, path, direction));
  }

  _handleClear(path) {
    this._applyLayout(clearSection(this._layout.root, path));
  }

  _handleMerge(path) {
    this._applyLayout(mergeSplit(this._layout.root, path));
  }

  _mountPicker() {
    let picker = this.querySelector('layout-component-picker-modal');
    if (!picker) {
      picker = document.createElement('layout-component-picker-modal');
      this.appendChild(picker);
      picker.addEventListener('component-pick', (e) => {
        this._handleComponentPick(e.detail.entry);
      });
    }
    return picker;
  }

  _openPicker(path, mode) {
    this._pickerPath = path;
    this._pickerMode = mode;
    this._openMenuPath = null;
    const leaf = getNodeAt(this._layout.root, path);
    const currentSection = leaf?.section || null;
    const picker = this._mountPicker();
    picker.open({
      title: mode === 'change' ? 'Change component' : 'Add a component',
      mode,
      catalog: this._componentCatalog,
      currentKey: sectionToCatalogKey(currentSection),
      canAdd: (entry) => canAddCatalogEntry(entry, this._layout.root, {
        excludeSectionId: currentSection?.id || null,
        mode
      })
    });
  }

  _handleComponentPick(entry) {
    const path = this._pickerPath;
    if (!path || !entry) return;

    const leaf = getNodeAt(this._layout.root, path);
    const currentSection = leaf?.section || null;
    let section;
    try {
      section = catalogEntryToSection(entry, this._layout.root, currentSection);
    } catch {
      return;
    }

    const wasAdd = !currentSection;
    const prevSectionId = currentSection?.id ?? null;
    const prevWidgetId = currentSection?.widgetId ?? null;
    this._pickerPath = null;
    this._pickerMode = null;

    if (prevSectionId && prevSectionId !== section.id) {
      this._content.delete(prevSectionId);
      this._instanceConfigValues.delete(prevSectionId);
      this._oauthStatus.delete(prevSectionId);
    }

    this._applyLayout(assignSection(this._layout.root, path, section));

    if (wasAdd) {
      this.dispatchEvent(new CustomEvent('layout-section-added', {
        bubbles: true,
        detail: { sectionId: section.id, type: section.type, widgetId: section.widgetId ?? null }
      }));
      return;
    }

    this.dispatchEvent(new CustomEvent('layout-section-added', {
      bubbles: true,
      detail: { sectionId: section.id, type: section.type, widgetId: section.widgetId ?? null }
    }));

    if (section.type === 'widget' && section.widgetId !== prevWidgetId) {
      this.dispatchEvent(new CustomEvent('layout-widget-change', {
        bubbles: true,
        detail: { sectionId: section.id, widgetId: section.widgetId }
      }));
    }
  }

  _hasAddableComponents() {
    return this._componentCatalog.some((entry) => canAddCatalogEntry(entry, this._layout.root, { mode: 'add' }));
  }

  _renderMenu(path, node) {
    const pathStr = path.join(',');
    const isOpen = this._openMenuPath === pathStr;
    const menuItems = [];

    if (node.type === 'leaf') {
      if (!node.section) {
        if (this._hasAddableComponents()) {
          menuItems.push(`
            <button type="button" class="layout-split-menu-item" data-action="open-picker" data-path="${pathStr}" data-picker-mode="add">
              Add component
            </button>
          `);
        } else {
          menuItems.push('<span class="layout-split-menu-empty">No components available</span>');
        }
      } else {
        menuItems.push(`
          <button type="button" class="layout-split-menu-item" data-action="open-picker" data-path="${pathStr}" data-picker-mode="change">
            Change component
          </button>
          <button type="button" class="layout-split-menu-item" data-action="split" data-path="${pathStr}" data-direction="row">
            Split horizontally
          </button>
          <button type="button" class="layout-split-menu-item" data-action="split" data-path="${pathStr}" data-direction="col">
            Split vertically
          </button>
          <button type="button" class="layout-split-menu-item layout-split-menu-item--danger" data-action="clear" data-path="${pathStr}">
            Remove block
          </button>
        `);
      }

      if (path.length > 0) {
        const parentPath = path.slice(0, -1).join(',');
        menuItems.push(`
          <button type="button" class="layout-split-menu-item layout-split-menu-item--danger" data-action="merge" data-path="${parentPath}">
            Remove split
          </button>
        `);
      }
    }

    return `
      <div class="layout-split-region-menu">
        <button
          type="button"
          class="layout-split-menu-toggle"
          data-action="toggle-menu"
          data-path="${pathStr}"
          aria-expanded="${isOpen}"
          aria-haspopup="true"
          aria-label="Layout options"
        >
          <span class="material-symbols-outlined" aria-hidden="true">menu</span>
        </button>
        <div class="layout-split-menu-panel${isOpen ? ' is-open' : ''}" ${isOpen ? '' : 'hidden'}>
          ${menuItems.join('')}
        </div>
      </div>
    `;
  }

  _renderLeafEditor(node, path) {
    const pathStr = path.join(',');
    if (!node.section) {
      const addDisabled = this._hasAddableComponents() ? '' : 'disabled';
      return `
        <div class="layout-split-leaf layout-split-leaf--empty card" data-path="${pathStr}">
          <div class="layout-split-leaf-toolbar">
            ${this._renderMenu(path, node)}
          </div>
          <div class="layout-split-empty-placeholder">
            <button type="button" class="btn-cta layout-split-add-component-btn" data-action="open-picker" data-path="${pathStr}" data-picker-mode="add" ${addDisabled}>
              Add a component
            </button>
            <p class="form-text">Or use the menu to split this region. <a href="${appendReturnTo('/docs/widgets/add-to-profile')}">Layout docs</a></p>
          </div>
        </div>
      `;
    }

    const { section } = node;
    const widget = section.type === 'widget' ? this._getWidgetMeta(section.widgetId) : null;
    const widgetOAuth = section.type === 'widget' ? this._renderWidgetOAuthPanel(section, widget) : '';
    const widgetConfig = section.type === 'widget' && widget?.configSchema?.properties
      ? this._renderWidgetConfigForm(section)
      : '';

    return `
      <div class="layout-split-leaf card" data-path="${pathStr}" data-section-id="${section.id}">
        <div class="layout-split-leaf-toolbar">
          ${this._renderMenu(path, node)}
        </div>
        ${widgetOAuth}
        ${widgetConfig}
        <div class="layout-split-editor-preview">${this._content.get(section.id) || ''}</div>
      </div>
    `;
  }

  _renderNodeEditor(node, path = []) {
    if (!node) return '';

    if (node.type === 'leaf') {
      return this._renderLeafEditor(node, path);
    }

    if (node.type === 'split') {
      const dirClass = node.direction === 'row'
        ? 'layout-split-node--row'
        : 'layout-split-node--col';
      return `
        <div class="layout-split-node ${dirClass}" data-path="${path.join(',')}">
          ${node.children.map((child, index) => `
            <div class="layout-split-pane">
              ${this._renderNodeEditor(child, [...path, index])}
            </div>
          `).join('')}
        </div>
      `;
    }

    return '';
  }

  render() {
    const hint = this._inline
      ? 'Use the menu on each block to split or swap components.'
      : 'Use the menu on each block to split regions or change components.';
    const editorClass = this._inline ? 'layout-split-editor layout-split-editor--inline' : 'layout-split-editor';

    let mount = this.querySelector('.layout-split-editor-mount');
    if (!mount) {
      this.innerHTML = '<div class="layout-split-editor-mount"></div>';
      mount = this.querySelector('.layout-split-editor-mount');
      this._mountPicker();
    }

    mount.innerHTML = `
      <div class="${editorClass}">
        <p class="form-text layout-split-editor-hint">${hint}</p>
        <div class="layout-split-editor-root">
          ${this._renderNodeEditor(this._layout.root)}
        </div>
        <p class="layout-split-editor-error form-text" role="alert"></p>
      </div>
    `;
  }

  _bindMountListeners() {
    const mount = this.querySelector('.layout-split-editor-mount');
    if (!mount || this._mountListenersBound) return;
    this._mountListenersBound = true;

    mount.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-action="toggle-menu"]');
      if (toggle) {
        e.stopPropagation();
        const path = toggle.dataset.path;
        this._openMenuPath = this._openMenuPath === path ? null : path;
        if (this._openMenuPath) {
          this._suppressOutsideClick = true;
          queueMicrotask(() => { this._suppressOutsideClick = false; });
        }
        this.render();
        return;
      }

      const pickerBtn = e.target.closest('[data-action="open-picker"]');
      if (pickerBtn) {
        e.stopPropagation();
        if (pickerBtn.disabled) return;
        this._openMenuPath = null;
        this._openPicker(this._parsePath(pickerBtn.dataset.path), pickerBtn.dataset.pickerMode || 'add');
        return;
      }

      const splitBtn = e.target.closest('[data-action="split"]');
      if (splitBtn) {
        this._handleSplit(this._parsePath(splitBtn.dataset.path), splitBtn.dataset.direction);
        return;
      }

      const clearBtn = e.target.closest('[data-action="clear"]');
      if (clearBtn) {
        this._handleClear(this._parsePath(clearBtn.dataset.path));
        return;
      }

      const mergeBtn = e.target.closest('[data-action="merge"]');
      if (mergeBtn) {
        this._handleMerge(this._parsePath(mergeBtn.dataset.path));
        return;
      }

      const disconnectBtn = e.target.closest('[data-action="oauth-disconnect"]');
      if (disconnectBtn) {
        e.preventDefault();
        this._handleOAuthDisconnect(disconnectBtn.dataset.sectionId);
        return;
      }

      const connectBtn = e.target.closest('[data-action="oauth-connect"]');
      if (connectBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (connectBtn.disabled) return;
        this.dispatchEvent(new CustomEvent('layout-oauth-connect', {
          bubbles: true,
          detail: {
            widgetId: parseInt(connectBtn.dataset.widgetId, 10),
            sectionId: connectBtn.dataset.sectionId,
            connectUrl: connectBtn.dataset.connectUrl
          }
        }));
      }
    });

    mount.addEventListener('input', (e) => {
      const input = e.target.closest('[data-widget-config-field]');
      if (!input) return;
      const sectionId = input.dataset.sectionId;
      const field = input.dataset.widgetConfigField;
      if (!sectionId || !field) return;
      if (!this._instanceConfigValues.has(sectionId)) {
        this._instanceConfigValues.set(sectionId, {});
      }
      this._instanceConfigValues.get(sectionId)[field] = input.value;
    });

    if (!this._outsideClickBound) {
      this._outsideClickBound = (e) => {
        if (this._suppressOutsideClick) return;
        if (!this._openMenuPath) return;
        if (!this.contains(e.target)) {
          this._openMenuPath = null;
          this.render();
        }
      };
      document.addEventListener('click', this._outsideClickBound);
    }
  }

  async _handleOAuthDisconnect(sectionId) {
    if (!sectionId) return;
    try {
      const res = await fetch(
        `/api/users/me/widget-instances/${encodeURIComponent(sectionId)}/oauth`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!res.ok) throw new Error('Disconnect failed');
      this._oauthStatus.set(sectionId, { connected: false });
      await this.refreshOAuthStatuses();
    } catch (err) {
      window.logger?.warn('OAuth disconnect failed:', err);
    }
  }

  disconnectedCallback() {
    if (this._outsideClickBound) {
      document.removeEventListener('click', this._outsideClickBound);
      this._outsideClickBound = null;
    }
  }

  _escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('layout-split-editor', LayoutSplitEditor);
